import CreateAccountContext, { ECreateAccountUiState } from '../../contexts/CreateAccountContext';
import common from '../../util/scss/common.module.scss';
import styles from './CreateAccountForm.module.scss';
import LegalAgreementsCheckboxes from '../LegalAgreementsCheckboxes/LegalAgreementsCheckboxes';
import PasswordValidator from '../PasswordValidator/PasswordValidator';
import PasswordInput from '../PasswordInput/PasswordInput';
import {
    Alert,
    MenuItem,
    TextField,
    Button,
    Icon,
    Divider,
    Box,
    InputAdornment,
    Link,
    useTheme,
    Stack,
    Typography,
} from '@mui/material';
import { FC, SyntheticEvent, useContext, useEffect, useState } from 'react';
import GlobalContext from '../../contexts/GlobalContext';
import { LoadingButton } from '@mui/lab';
import { Tooltip } from '@mui/material';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import ScreenHeader from '../ScreenHeader/ScreenHeader';
import { useHistory } from 'react-router-dom';
import { TLoginRouterState } from '../../contexts/LoginContext';
import { EMixpanelEvent, ERoutes } from '../../util/constants';
import mixpanel from 'mixpanel-browser';

declare module '@mui/material/Icon' {
    interface IconPropsSizeOverrides {
        xSmall: true;
    }
}

enum ELocalUiState {
    ready,
    loading,
    error,
}

const CreateAccountForm: FC = () => {
    const { coBranding, language, countriesList, returnUrlParameter } = useContext(GlobalContext);
    const {
        isPrePopulated,
        firstName,
        setFirstName,
        firstNameIsValid,
        firstNameValidationMessage,
        lastName,
        setLastName,
        lastNameIsValid,
        lastNameValidationMessage,
        email,
        emailIsValid,
        setEmail,
        emailValidationMessage,
        password,
        passwordIsValid,
        setPassword,
        country,
        setCountry,
        countryIsValid,
        submitCreateAccountForm,
        addFamily,
        skipAddFamily,
        setCreateAccountUiState,
        tosAgreed,
        setTosAgreed,
        tosIsValid,
        marketingOptIn,
        setMarketingOptIn,
        createAccountUiState,
    } = useContext(CreateAccountContext);
    const [localUiState, setLocalUiState] = useState(ELocalUiState.ready);
    const [errorMessage, setErrorMessage] = useState(language.validation.genericError);
    const [showPasswordValidation, setShowPasswordValidation] = useState(false);
    const theme = useTheme();
    const history = useHistory();

    const handleChangeEmail = () => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_create_account_email_edit' });
        const loginRouterState: TLoginRouterState = {
            prePopulateForm: { username: email },
        };
        history.push({
            pathname: `${ERoutes.login}`,
            search: `?${returnUrlParameter}`,
            state: loginRouterState,
        });
    };

    const handleAddFamily = (event: SyntheticEvent) => {
        event.preventDefault();
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_create_account_add_family' });
        addFamily();
    };

    const handleFamilyFirstSkipFamily = () => {
        mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_create_account' });
        skipAddFamily();
    };

    const handleShowPasswordValidation = () => {
        !showPasswordValidation && setShowPasswordValidation(true);
    };

    const handleSubmitCreateAccountForm = async (event: SyntheticEvent) => {
        event.preventDefault();
        setLocalUiState(ELocalUiState.loading);
        try {
            await submitCreateAccountForm();
            mixpanel.track(EMixpanelEvent.interacted, {
                key: 'consumer_create_account',
                marketingOptIn: marketingOptIn,
            });
        } catch (error) {
            if (error.cause === 'invalid') {
                setLocalUiState(ELocalUiState.ready);
            } else {
                if (error.cause === 409) {
                    setErrorMessage(language.validation.accountExists.header);
                } else {
                    setErrorMessage(language.validation.genericError);
                }
                mixpanel.track(EMixpanelEvent.actionFailed, { key: 'consumer_create_account' });
                setLocalUiState(ELocalUiState.error);
            }
        }
    };

    // If navigating from the confirm page (i.e. clicking back button in browser), reset ui state to ready
    useEffect(() => {
        if (createAccountUiState === ECreateAccountUiState.confirm) {
            setCreateAccountUiState(ECreateAccountUiState.ready);
        }
    }, [createAccountUiState]);

    useEffect(() => {
        mixpanel.track(EMixpanelEvent.viewed, { key: 'consumer_create_account', marketingOptIn: marketingOptIn });
    }, []);

    return (
        <PageLayoutV2 testId="create-account">
            <ScreenHeader
                title={
                    coBranding.isDefaultFamilyAccount
                        ? language.createAccount.headerFamilyFirst
                        : language.createAccount.header
                }
                desc={
                    coBranding.isDefaultFamilyAccount
                        ? language.createAccount.descFamilyFirst
                        : language.createAccount.desc
                }
            />
            {localUiState === ELocalUiState.error && (
                <section className={common.genericError}>
                    <Alert severity="error">{errorMessage}</Alert>
                </section>
            )}
            {coBranding.isDefaultFamilyAccount && (
                <Stack direction={'row'} sx={{ my: 2, alignItems: 'center' }}>
                    <Typography component={'h3'} variant={'subtitle2'}>
                        {language.createFamilyAccount.primaryUser.title}
                    </Typography>
                    <Tooltip title={language.createFamilyAccount.details} enterTouchDelay={50} leaveTouchDelay={7000}>
                        <Icon color="action" fontSize={'xSmall'} sx={{ ml: 1 }}>
                            info
                        </Icon>
                    </Tooltip>
                </Stack>
            )}
            <form>
                <main className={styles.createAccountForm}>
                    <TextField
                        label={language.createAccount.form.email}
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        error={!emailIsValid}
                        helperText={!emailIsValid && emailValidationMessage}
                        fullWidth
                        id="email"
                        size="small"
                        sx={{ mb: 3 }}
                        disabled={isPrePopulated ? true : undefined}
                        autoFocus={!isPrePopulated ? true : undefined}
                        InputProps={
                            isPrePopulated
                                ? {
                                      endAdornment: (
                                          <InputAdornment position="end">
                                              <Link
                                                  onClick={() => {
                                                      handleChangeEmail();
                                                  }}
                                                  color={theme.palette.action.active}
                                                  sx={{ typography: 'subtitle2', cursor: 'pointer' }}
                                                  data-testid="change-email"
                                              >
                                                  {language.common.edit}
                                              </Link>
                                          </InputAdornment>
                                      ),
                                  }
                                : undefined
                        }
                    />
                    <TextField
                        label={language.createAccount.form.firstName}
                        value={firstName}
                        autoFocus={isPrePopulated ? true : undefined}
                        error={!firstNameIsValid}
                        helperText={!firstNameIsValid && firstNameValidationMessage}
                        onChange={(event) => setFirstName(event.target.value)}
                        fullWidth
                        id="firstName"
                        size="small"
                        sx={{ mb: 1 }}
                    />
                    <TextField
                        label={language.createAccount.form.lastName}
                        value={lastName}
                        error={!lastNameIsValid}
                        helperText={!lastNameIsValid && lastNameValidationMessage}
                        onChange={(event) => setLastName(event.target.value)}
                        fullWidth
                        id="lastName"
                        size="small"
                        sx={{ mb: 3 }}
                    />
                    <PasswordInput
                        value={password}
                        error={!passwordIsValid}
                        label={language.createAccount.form.password}
                        onChange={(event) => setPassword(event.target.value)}
                        onFocus={handleShowPasswordValidation}
                        helperText={!passwordIsValid && language.validation.password.invalid}
                        id="password"
                        size="small"
                        sx={{ mb: 3 }}
                        onShowPassword={() => {
                            mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_create_account_password_show' });
                        }}
                        onHidePassword={() => {
                            mixpanel.track(EMixpanelEvent.interacted, { key: 'consumer_create_account_password_hide' });
                        }}
                    />
                    {showPasswordValidation && (
                        <Box sx={{ mb: 3 }} data-testid="password-validator">
                            <PasswordValidator password={password} />
                        </Box>
                    )}
                    <TextField
                        select
                        label={language.createAccount.form.country}
                        value={country}
                        onChange={(event) => setCountry(event.target.value)}
                        fullWidth
                        id="country"
                        size="small"
                        error={!countryIsValid}
                    >
                        {countriesList.map((country) => (
                            <MenuItem key={country.code} value={country.code} id={country.code}>
                                {country.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    {!coBranding.isDefaultFamilyAccount && (
                        <>
                            <Box sx={{ my: 2 }}>
                                <LegalAgreementsCheckboxes
                                    tosAgreed={tosAgreed}
                                    setTosAgreed={setTosAgreed}
                                    marketingOptIn={marketingOptIn}
                                    setMarketingOptIn={setMarketingOptIn}
                                    tosIsValid={tosIsValid}
                                />
                            </Box>
                            <Divider sx={{ mt: 2, mb: 3 }} />
                        </>
                    )}
                    {coBranding.isDefaultFamilyAccount ? (
                        <>
                            <Button
                                type="submit"
                                size="large"
                                fullWidth
                                onClick={handleAddFamily}
                                variant="contained"
                                sx={{ mt: 2 }}
                            >
                                {language.createAccount.createFamilyCta}
                            </Button>
                            <Divider sx={{ my: 3, typography: 'body2' }}>{language.common.or}</Divider>
                            <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
                                <Link
                                    component={'button'}
                                    type={'button'}
                                    variant="subtitle2"
                                    onClick={handleFamilyFirstSkipFamily}
                                    sx={{ display: 'block' }}
                                >
                                    {language.createAccount.familyFirstSkipFamily}
                                </Link>
                            </Stack>
                        </>
                    ) : (
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            size="large"
                            fullWidth
                            disabled={localUiState === ELocalUiState.loading}
                            onClick={handleSubmitCreateAccountForm}
                            loading={localUiState === ELocalUiState.loading}
                        >
                            {language.createAccount.createAccountCTA}
                        </LoadingButton>
                    )}
                </main>
            </form>
        </PageLayoutV2>
    );
};

export default CreateAccountForm;
